/**
 * Stacked Executive Dashboard — Phase C (Multi-Tenant)
 *
 * Shows rollup totals for an entire tenant (all locations combined) plus an
 * expandable breakdown per location. Accessible to tenant admins, admins,
 * managers, and super_admins. Only useful when a tenant has 2+ locations.
 *
 * Backend: GET /api/ops/exec-dashboard/tenant?tenant_id=X
 *   - Returns {tenant, locations: [{id, name, data}], rollup}
 *   - For non-super-admin, tenant_id auto-derives from user.tenant_id
 *   - For super-admin, requires explicit tenant_id (from URL ?tenant_id= or state)
 *
 * Per-location drill-down re-uses the existing /exec-dashboard cached payload
 * so a deep-dive into any one location is 1-click away.
 */
import { useEffect, useState, useCallback, Fragment } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Layers, Building2, ChevronRight, ChevronDown, RefreshCw, Loader2,
  DollarSign, Users, Dog, ArrowUp, ArrowDown, MapPin, TrendingUp,
  AlertCircle, BedDouble, Eye, BarChart3,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Backend now ships YoY for all six ranges (Today / MTD / QTD / Last Month / YTD / Last Year Full).
const RANGE_OPTIONS = [
  { value: "ytd",        label: "Year to Date",      field: "ytd",        py: "ytd_py",        change: "ytd_change",        supported: true },
  { value: "qtd",        label: "Quarter to Date",   field: "qtd",        py: "qtd_py",        change: "qtd_change",        supported: true },
  { value: "mtd",        label: "Month to Date",     field: "mtd",        py: "mtd_py",        change: "mtd_change",        supported: true },
  { value: "today",      label: "Today",             field: "today",      py: "today_py",      change: "today_change",      supported: true },
  { value: "last_month", label: "Last Month",        field: "last_month", py: "last_month_py", change: "last_month_change", supported: true },
  { value: "last_year",  label: "Last Year (Full)",  field: "last_year",  py: "last_year_py",  change: "last_year_change",  supported: true },
];

const fmtFull = (n) =>
  n == null ? "$0" : `$${Number(n).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
const fmtK = (n) =>
  n == null ? "$0" : n >= 1000 ? `$${(n / 1000).toFixed(0)}K` : `$${Math.round(n)}`;

const ChangeChip = ({ value, size = "sm" }) => {
  if (value == null) return null;
  const positive = value > 0;
  const neutral = value === 0;
  const Icon = positive ? ArrowUp : ArrowDown;
  if (neutral) return <span className="text-xs text-gray-400">—</span>;
  const color = positive ? "text-emerald-600 bg-emerald-50" : "text-red-600 bg-red-50";
  const padding = size === "lg" ? "px-2 py-0.5 text-xs" : "px-1.5 py-0.5 text-[11px]";
  return (
    <span className={`inline-flex items-center gap-0.5 rounded font-bold ${color} ${padding}`}>
      <Icon className={size === "lg" ? "h-3.5 w-3.5" : "h-3 w-3"} />
      {Math.abs(value).toFixed(1)}%
    </span>
  );
};

import { LineChart, Line, ResponsiveContainer, Tooltip as RcTooltip } from "recharts";
// ── KPI Card (matches ExecDashboard styling for visual consistency) ─────
const KPICard = ({ title, value, subtitle, change, icon: Icon, color = "blue", sparkline }) => {
  const colorMap = {
    blue: "from-blue-600 to-blue-700",
    emerald: "from-emerald-600 to-emerald-700",
    amber: "from-amber-500 to-amber-600",
    indigo: "from-indigo-600 to-indigo-700",
  };
  const lineColorMap = {
    blue: "#2563eb", emerald: "#059669", amber: "#d97706", indigo: "#4f46e5",
  };
  return (
    <Card className="overflow-hidden border-0 shadow-md">
      <div className={`h-1.5 bg-gradient-to-r ${colorMap[color] || colorMap.blue}`} />
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">{title}</span>
          {Icon && <Icon className="h-4 w-4 text-gray-400" />}
        </div>
        <div className="text-2xl lg:text-3xl font-extrabold text-gray-900 tracking-tight">{value}</div>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          {subtitle && <span className="text-xs text-gray-500">{subtitle}</span>}
          {change != null && <ChangeChip value={change} />}
        </div>
        {sparkline && sparkline.length > 0 && (
          <Sparkline data={sparkline} color={lineColorMap[color] || lineColorMap.blue} />
        )}
      </CardContent>
    </Card>
  );
};

// ── Sparkline: trailing months current vs PY, used in revenue KPI cards ──
const Sparkline = ({ data, color }) => {
  // data shape: [{label, curr, py}, ...]
  const fmtCurrency = v => `$${(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  return (
    <div className="mt-3 -mx-1">
      <div className="flex items-center justify-between text-[9px] uppercase tracking-wider text-gray-400 px-1 mb-0.5">
        <span>Trailing 6 mo</span>
        <span className="flex items-center gap-2">
          <span className="flex items-center gap-1">
            <span className="inline-block w-2 h-0.5" style={{ backgroundColor: color }} /> Curr
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block w-2 h-0.5 bg-gray-300" /> PY
          </span>
        </span>
      </div>
      <div className="h-12">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
            <Line type="monotone" dataKey="py" stroke="#cbd5e1" strokeWidth={1.5} strokeDasharray="3 2" dot={false} isAnimationActive={false} />
            <Line type="monotone" dataKey="curr" stroke={color} strokeWidth={2} dot={false} isAnimationActive={false} />
            <RcTooltip
              cursor={{ stroke: "#e5e7eb", strokeWidth: 1 }}
              wrapperStyle={{ zIndex: 50 }}
              contentStyle={{ fontSize: 11, padding: "4px 8px", border: "1px solid #e5e7eb", borderRadius: 6 }}
              labelFormatter={(_, items) => items?.[0]?.payload?.label || ""}
              formatter={(v, name) => [fmtCurrency(v), name === "curr" ? "Current" : "Prior Year"]}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="flex justify-between text-[9px] text-gray-400 px-1 mt-0.5">
        {data.map((pt, i) => (
          <span key={i}>{pt.label}</span>
        ))}
      </div>
    </div>
  );
};


export default function StackedExecDashboard() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(new Set());
  const [view, setView] = useState("rollup"); // rollup | comparison
  const [range, setRange] = useState("ytd");  // YTD / MTD / Today / (unsupported QTD/LM/LY)

  // For super_admin, also fetch the list of tenants to offer a picker
  const cachedUser = JSON.parse(localStorage.getItem("cachedUser") || "{}");
  const isSuperAdmin = cachedUser.role === "super_admin";
  const urlTenantId = params.get("tenant_id");
  const [selectedTenantId, setSelectedTenantId] = useState(urlTenantId || "");
  const [availableTenants, setAvailableTenants] = useState([]);

  const fetchData = useCallback(async (tenantId) => {
    setRefreshing(true);
    setError(null);
    try {
      // For non-super-admins, do a fast precheck first: if the tenant has
      // fewer than 2 locations, short-circuit with a friendly message instead
      // of making the user wait on the heavy per-location dashboard fetch.
      if (!isSuperAdmin) {
        try {
          const me = await axios.get(`${API}/tenants/me`, { timeout: 10000 });
          if ((me.data?.location_count ?? 0) < 2) {
            setData({
              tenant: { id: me.data?.id, name: me.data?.name },
              locations: [],
              rollup: null,
              _single_location: true,
            });
            return;
          }
        } catch (_) {
          // fall through — let the main call surface any auth/role errors
        }
      }

      const url = `${API}/ops/exec-dashboard/tenant${tenantId ? `?tenant_id=${tenantId}` : ""}`;
      const res = await axios.get(url, { timeout: 120000 });
      setData(res.data);
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to load tenant dashboard";
      setError(msg);
      if (err.response?.status === 403) {
        toast.error(msg);
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    // Super-admin: load tenant list for picker
    if (isSuperAdmin) {
      axios.get(`${API}/admin/tenants`)
        .then(res => {
          const multi = (res.data.tenants || []).filter(t => t.location_count >= 1);
          setAvailableTenants(multi);
          // Auto-pick first tenant if none selected
          if (!selectedTenantId && multi.length > 0) {
            const firstMulti = multi.find(t => t.location_count >= 2) || multi[0];
            setSelectedTenantId(firstMulti.id);
          }
        })
        .catch(() => {});
    }
  }, [isSuperAdmin, selectedTenantId]);

  useEffect(() => {
    if (isSuperAdmin && !selectedTenantId) return; // wait for tenant pick
    fetchData(isSuperAdmin ? selectedTenantId : null);
  }, [fetchData, isSuperAdmin, selectedTenantId]);

  const toggleExpand = (id) => {
    const next = new Set(expanded);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExpanded(next);
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
              <div>
                <div className="font-bold text-red-900">Cannot load tenant dashboard</div>
                <p className="text-sm text-red-800 mt-1">{error}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!data) return null;

  // Single-location tenants don't need the Org View. Show a friendly card
  // pointing them back to the regular Exec Dashboard instead of letting them
  // sit on a heavy multi-location aggregate that has nothing to roll up.
  if (data._single_location) {
    return (
      <div className="p-6 max-w-2xl mx-auto" data-testid="org-view-single-location-guard">
        <Card className="border-indigo-100">
          <CardContent className="p-6">
            <div className="flex items-start gap-3">
              <Layers className="h-5 w-5 text-indigo-600 mt-0.5" />
              <div className="flex-1">
                <div className="font-bold text-gray-900">Org View is for multi-location tenants</div>
                <p className="text-sm text-gray-600 mt-1">
                  {data.tenant?.name || "Your tenant"} currently has a single location, so there's
                  nothing to roll up here. Head back to the standard Executive Dashboard for the
                  full picture.
                </p>
                <Button
                  size="sm"
                  className="mt-4 bg-indigo-600 hover:bg-indigo-700"
                  data-testid="back-to-exec-dashboard-btn"
                  onClick={() => navigate("/exec-dashboard")}
                >
                  Back to Executive Dashboard
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { tenant, locations = [], rollup } = data;
  const singleLocation = locations.length <= 1;

  const rangeCfg = RANGE_OPTIONS.find(r => r.value === range) || RANGE_OPTIONS[0];
  // Map range to the specific rollup revenue slice (ytd/mtd/today)
  const revSlice = rangeCfg.supported ? rangeCfg.field : "ytd";   // e.g. 'ytd' | 'mtd' | 'today'
  const changeSlice = rangeCfg.supported ? rangeCfg.change : "ytd_change";

  // Step-down: when user picks a wide range, show a narrower "what's happening
  // right now inside it" secondary card; when they pick narrow, show the wider
  // context they zoomed out from. Keeps the second KPI useful at every range.
  const stepDownMap = {
    ytd: "mtd", qtd: "mtd", last_year: "ytd",
    last_month: "mtd", mtd: "today", today: "mtd",
  };
  const stepDownKey = stepDownMap[revSlice] || "mtd";
  const stepDownLabel = ({ ytd: "YTD", qtd: "QTD", mtd: "MTD", today: "Today", last_month: "Last Month", last_year: "Last Year" })[stepDownKey];

  return (
    <div className="p-4 lg:p-6 max-w-7xl mx-auto space-y-6" data-testid="stacked-exec-page">
      {/* Header — title + tenant picker (super_admin) on the left, action ribbon on the right */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-11 w-11 rounded-lg bg-gradient-to-br from-indigo-600 to-blue-700 flex items-center justify-center shadow flex-shrink-0">
            <Building2 className="h-5 w-5 text-white" />
          </div>
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wider text-gray-500 font-semibold">Org Exec Dashboard</div>
            {/* For super_admin: tenant picker IS the title (primary scope selector). Others: static name. */}
            {isSuperAdmin && availableTenants.length > 0 ? (
              <div className="flex items-center gap-2 flex-wrap">
                <div className="relative inline-block">
                  <select
                    value={selectedTenantId}
                    onChange={e => {
                      setSelectedTenantId(e.target.value);
                      setData(null);
                      setLoading(true);
                    }}
                    className="appearance-none text-xl lg:text-2xl font-bold text-gray-900 bg-transparent border-0 border-b-2 border-dashed border-indigo-300 hover:border-indigo-600 focus:border-indigo-600 focus:outline-none pr-7 cursor-pointer"
                    data-testid="tenant-picker"
                    title="Switch tenant (super admin)"
                  >
                    {availableTenants.map(t => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="h-4 w-4 text-indigo-500 absolute right-1 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>
                <span className="text-[10px] uppercase tracking-wider font-bold text-indigo-600 bg-indigo-50 border border-indigo-200 px-1.5 py-0.5 rounded">
                  Super Admin
                </span>
              </div>
            ) : (
              <div className="text-xl lg:text-2xl font-bold text-gray-900">{tenant?.name}</div>
            )}
            <div className="text-xs text-gray-500 mt-0.5">
              {locations.length} location{locations.length === 1 ? "" : "s"}
              {rollup ? ` · ${rollup.location_count} with data` : ""}
              {rollup?.locations_with_errors > 0 && (
                <span className="text-amber-600 ml-1">
                  · {rollup.locations_with_errors} pending data
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Action ribbon — grouped: [view toggle][range] | [refresh][single-view] */}
        <div className="flex items-center gap-2 flex-wrap">
          {locations.length >= 2 && (
            <div className="inline-flex rounded-lg border bg-white p-1 shadow-sm">
              <button
                data-testid="view-toggle-rollup"
                onClick={() => setView("rollup")}
                className={`px-3 py-1.5 text-xs font-semibold rounded transition-colors ${view === "rollup" ? "bg-indigo-600 text-white" : "text-gray-600 hover:bg-gray-50"}`}
              >
                <Layers className="h-3.5 w-3.5 inline mr-1" /> Roll-Up
              </button>
              <button
                data-testid="view-toggle-comparison"
                onClick={() => setView("comparison")}
                className={`px-3 py-1.5 text-xs font-semibold rounded transition-colors ${view === "comparison" ? "bg-indigo-600 text-white" : "text-gray-600 hover:bg-gray-50"}`}
              >
                <BarChart3 className="h-3.5 w-3.5 inline mr-1" /> Side-by-Side
              </button>
            </div>
          )}
          <Select value={range} onValueChange={setRange}>
            <SelectTrigger className="w-40 h-9 text-xs bg-white" data-testid="range-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RANGE_OPTIONS.map(r => (
                <SelectItem key={r.value} value={r.value} disabled={!r.supported}>
                  {r.label}{!r.supported && " (soon)"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="h-6 w-px bg-gray-200 mx-1 hidden sm:block" />
          <Button
            variant="outline"
            size="icon"
            onClick={() => fetchData(isSuperAdmin ? selectedTenantId : null)}
            disabled={refreshing}
            data-testid="refresh-stacked-btn"
            title="Refresh data"
            aria-label="Refresh data"
            className="h-9 w-9"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
          </Button>
          <Link to="/exec-dashboard">
            <Button variant="outline" data-testid="single-loc-view-btn">
              Single-Location View
            </Button>
          </Link>
        </div>
      </div>

      {singleLocation && (
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertCircle className="h-4 w-4 text-amber-700 mt-0.5" />
            <div className="text-xs text-amber-900">
              This tenant has only one location. The stacked view is most useful once you onboard a second location
              under this org. In the meantime, you'll see the same data as the
              <Link to="/exec-dashboard" className="underline font-semibold ml-1">single-location dashboard</Link>.
            </div>
          </CardContent>
        </Card>
      )}

      {/* Rollup KPI Strip (driven by selected range) */}
      {rollup && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <KPICard
            title={`Revenue ${rangeCfg.label}`}
            value={fmtFull(rollup.revenue[revSlice])}
            subtitle={`All locations · vs ${fmtFull(rollup.revenue[`${revSlice}_py`] || 0)} last year`}
            change={rollup.revenue[changeSlice]}
            icon={DollarSign}
            color="emerald"
            sparkline={rollup.revenue.monthly_trend}
          />
          <KPICard
            title={`Revenue ${stepDownLabel}`}
            value={fmtFull(rollup.revenue[stepDownKey])}
            subtitle={
              rollup.revenue[`${stepDownKey}_py`] != null
                ? `vs ${fmtFull(rollup.revenue[`${stepDownKey}_py`])} last year`
                : "Step-down view"
            }
            change={rollup.revenue[`${stepDownKey}_change`]}
            icon={TrendingUp}
            color="indigo"
            sparkline={rollup.revenue.monthly_trend}
          />
          <KPICard
            title="Dogs On-Site"
            value={rollup.occupancy.current.total_active}
            subtitle={`${rollup.occupancy.current.boarding} boarding + ${rollup.occupancy.current.daycare} daycare`}
            change={rollup.occupancy.total_change}
            icon={Dog}
            color="amber"
          />
          <KPICard
            title="Leads YTD"
            value={rollup.leads.total.toLocaleString()}
            subtitle={
              rollup.leads.conversion_rate != null
                ? `${rollup.leads.converted} converted · ${rollup.leads.conversion_rate}% rate`
                : `${rollup.leads.converted} converted`
            }
            icon={Users}
            color="blue"
          />
        </div>
      )}

      {/* Per-Location Breakdown (Roll-Up view) */}
      {view === "rollup" && (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center justify-between">
            <span>Breakdown by Location</span>
            <span className="text-xs font-normal text-gray-500">Click any row to expand</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm tabular-nums">
              <thead>
                <tr className="border-b text-xs uppercase tracking-wider text-gray-500">
                  <th className="text-left py-2 font-semibold w-8"></th>
                  <th className="text-left py-2 font-semibold">Location</th>
                  <th className="text-right py-2 pr-4 font-semibold">Revenue {rangeCfg.label.replace("Year to Date", "YTD").replace("Month to Date", "MTD").replace("Quarter to Date", "QTD")}</th>
                  <th className="text-right py-2 pr-4 font-semibold">{stepDownLabel}</th>
                  <th className="text-right py-2 pr-4 font-semibold">Today</th>
                  <th className="text-right py-2 pr-4 font-semibold">Dogs</th>
                  <th className="text-right py-2 pr-4 font-semibold">Leads</th>
                  <th className="text-right py-2 pr-2 font-semibold"></th>
                </tr>
              </thead>
              <tbody>
                {locations.map(loc => {
                  const isExp = expanded.has(loc.id);
                  const d = loc.data || {};
                  const err = d.error;
                  const rev = d.kpis?.revenue || {};
                  const occ = d.kpis?.occupancy?.current || {};
                  const lds = d.leads || {};
                  return (
                    <Fragment key={loc.id}>
                      <tr
                        className={`border-b cursor-pointer transition-colors ${err ? "bg-amber-50/40" : "hover:bg-indigo-50"}`}
                        onClick={() => toggleExpand(loc.id)}
                        data-testid={`row-${loc.id}`}
                      >
                        <td className="py-3 pl-2">
                          {isExp
                            ? <ChevronDown className="h-4 w-4 text-indigo-600" />
                            : <ChevronRight className="h-4 w-4 text-gray-400" />}
                        </td>
                        <td className="py-3">
                          <div className="flex items-center gap-2">
                            <MapPin className="h-3.5 w-3.5 text-gray-400" />
                            <span className="font-medium text-gray-900">{loc.name}</span>
                            {loc.city && <span className="text-xs text-gray-400">· {loc.city}</span>}
                          </div>
                        </td>
                        {err ? (
                          <td className="py-3 pr-4 text-xs text-amber-700 italic" colSpan={6}>
                            ⚠ {err}
                          </td>
                        ) : (
                          <>
                            <td className="text-right py-3 pr-4 font-semibold">
                              <div>{fmtFull(rev[revSlice])}</div>
                              {rev[`${revSlice}_change`] != null && (
                                <div className="mt-0.5 flex justify-end">
                                  <ChangeChip value={rev[`${revSlice}_change`]} />
                                </div>
                              )}
                            </td>
                            <td className="text-right py-3 pr-4">
                              <div className="font-medium">{fmtK(rev[stepDownKey])}</div>
                              {rev[`${stepDownKey}_change`] != null && (
                                <div className="mt-0.5 flex justify-end">
                                  <ChangeChip value={rev[`${stepDownKey}_change`]} />
                                </div>
                              )}
                            </td>
                            <td className="text-right py-3 pr-4">
                              <div>{fmtK(rev.today)}</div>
                              {rev.today_change != null && (
                                <div className="mt-0.5 flex justify-end">
                                  <ChangeChip value={rev.today_change} />
                                </div>
                              )}
                            </td>
                            <td className="text-right py-3 pr-4">
                              <div className="font-semibold">{occ.total_active ?? 0}</div>
                              <div className="text-[10px] text-gray-400">
                                {occ.boarding || 0} + {occ.daycare || 0}
                              </div>
                            </td>
                            <td className="text-right py-3 pr-4">{lds.total_leads ?? 0}</td>
                            <td className="text-right py-3 pr-2">
                              <Link to={`/exec-dashboard?location=${loc.id}`} onClick={e => e.stopPropagation()}>
                                <Button size="sm" variant="outline" className="h-7 text-xs">
                                  <Eye className="h-3.5 w-3.5 mr-1" /> Open
                                </Button>
                              </Link>
                            </td>
                          </>
                        )}
                      </tr>
                      {isExp && !err && (
                        <tr className="bg-slate-50 border-b">
                          <td colSpan={8} className="px-4 py-4">
                            <LocationDrillDown loc={loc} range={range} rangeCfg={rangeCfg} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
                {rollup && (
                  <tr className="bg-indigo-50 font-bold">
                    <td className="py-3"></td>
                    <td className="py-3 text-indigo-900">Total</td>
                    <td className="text-right py-3 pr-4 text-indigo-900">
                      <div>{fmtFull(rollup.revenue[revSlice])}</div>
                      {rollup.revenue[changeSlice] != null && (
                        <div className="mt-0.5 flex justify-end">
                          <ChangeChip value={rollup.revenue[changeSlice]} />
                        </div>
                      )}
                    </td>
                    <td className="text-right py-3 pr-4 text-indigo-900">
                      <div>{fmtK(rollup.revenue[stepDownKey])}</div>
                      {rollup.revenue[`${stepDownKey}_change`] != null && (
                        <div className="mt-0.5 flex justify-end">
                          <ChangeChip value={rollup.revenue[`${stepDownKey}_change`]} />
                        </div>
                      )}
                    </td>
                    <td className="text-right py-3 pr-4 text-indigo-900">{fmtK(rollup.revenue.today)}</td>
                    <td className="text-right py-3 pr-4 text-indigo-900">{rollup.occupancy.current.total_active}</td>
                    <td className="text-right py-3 pr-4 text-indigo-900">{rollup.leads.total}</td>
                    <td></td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Side-by-Side comparison view */}
      {view === "comparison" && locations.length >= 2 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Side-by-Side Performance</CardTitle>
            <p className="text-xs text-gray-500">
              Bars normalized to the top performer per metric. Vertical tick = last year same period. Δ = year-over-year.
            </p>
          </CardHeader>
          <CardContent>
            <ComparisonTable locations={locations} revSlice={revSlice} stepDownKey={stepDownKey} rangeLabel={rangeCfg.label} stepDownLabel={stepDownLabel} />
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ── Drill-down: revenue detail + occupancy + YoY for one location ───────
function LocationDrillDown({ loc, range, rangeCfg }) {
  const d = loc.data || {};
  const rev = d.kpis?.revenue || {};
  const occ = d.kpis?.occupancy?.current || {};
  const occPy = d.kpis?.occupancy?.prior_year || {};

  // Spotlight the chosen range in bolder styling, others remain as context.
  // Order: time-narrowest at top → broadest at bottom.
  const rows = [
    { key: "today",      label: "Today" },
    { key: "mtd",        label: "Month to Date" },
    { key: "qtd",        label: "Quarter to Date" },
    { key: "last_month", label: "Last Month" },
    { key: "ytd",        label: "Year to Date" },
    { key: "last_year",  label: "Last Year (Full)" },
  ];
  const activeKey = rangeCfg?.supported ? range : "ytd";

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* Revenue YoY */}
      <div className="bg-white rounded-lg border p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <DollarSign className="h-4 w-4 text-emerald-600" />
            <h4 className="font-semibold text-gray-900">Revenue · YoY</h4>
          </div>
          <span className="text-[11px] text-gray-500">
            Spotlight: <strong className="text-indigo-700">{rangeCfg?.label}</strong>
          </span>
        </div>
        <div className="space-y-3 text-sm">
          {rows.map(r => (
            <RevenueRow
              key={r.key}
              label={r.label}
              cur={rev[r.key]}
              py={rev[`${r.key}_py`]}
              change={rev[`${r.key}_change`]}
              highlighted={r.key === activeKey}
            />
          ))}
          {!rangeCfg?.supported && (
            <div className="mt-2 rounded border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-900">
              <strong>{rangeCfg?.label}</strong> needs backend extension — showing YTD spotlight as fallback.
            </div>
          )}
        </div>
      </div>

      {/* Occupancy */}
      <div className="bg-white rounded-lg border p-4">
        <div className="flex items-center gap-2 mb-3">
          <BedDouble className="h-4 w-4 text-blue-600" />
          <h4 className="font-semibold text-gray-900">Occupancy Detail</h4>
        </div>
        <div className="space-y-3 text-sm">
          <OccRow label="Boarding" cur={occ.boarding ?? 0} py={occPy.boarding ?? 0} />
          <OccRow label="Daycare"  cur={occ.daycare ?? 0}  py={occPy.daycare ?? 0} />
          <div className="border-t pt-2 flex justify-between items-center">
            <span className="text-xs text-gray-500">Check-ins today</span>
            <span className="font-semibold text-gray-900">{occ.check_ins ?? 0}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-xs text-gray-500">Check-outs today</span>
            <span className="font-semibold text-gray-900">{occ.check_outs ?? 0}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function RevenueRow({ label, cur, py, change, highlighted = false }) {
  return (
    <div
      className={`flex items-center justify-between gap-3 ${
        highlighted ? "bg-indigo-50 border border-indigo-200 rounded-md px-2 py-1.5 -mx-2" : ""
      }`}
    >
      <span className={`text-xs flex-shrink-0 ${highlighted ? "text-indigo-700 font-bold uppercase tracking-wider" : "text-gray-500"}`}>
        {label}
      </span>
      <div className="flex items-center gap-3">
        <span className="text-xs text-gray-400">vs {fmtK(py)}</span>
        <span className={`${highlighted ? "text-lg font-extrabold" : "font-semibold"} text-gray-900 min-w-[70px] text-right tabular-nums`}>
          {fmtFull(cur)}
        </span>
        <ChangeChip value={change} />
      </div>
    </div>
  );
}

function OccRow({ label, cur, py }) {
  const change = py > 0 ? ((cur - py) / py * 100) : null;
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-xs text-gray-500">{label}</span>
      <div className="flex items-center gap-3">
        <span className="text-xs text-gray-400">LY {py}</span>
        <span className="font-semibold text-gray-900 min-w-[40px] text-right">{cur}</span>
        {change != null && <ChangeChip value={Math.round(change * 10) / 10} />}
      </div>
    </div>
  );
}

// ── ComparisonTable: bar with YoY tick + per-location YoY Δ ─────────────
const BAR_COLORS = ["bg-indigo-500", "bg-cyan-500", "bg-emerald-500", "bg-amber-500", "bg-rose-500"];
const DOT_COLORS = ["bg-indigo-600", "bg-cyan-500", "bg-emerald-500", "bg-amber-500", "bg-rose-500"];

function ComparisonTable({ locations, revSlice = "ytd", stepDownKey = "mtd", rangeLabel = "Year to Date", stepDownLabel = "MTD" }) {
  // Only include locations with valid data
  const valid = locations.filter(l => l.data && !l.data.error && l.data.kpis);

  // Metrics derived from real payload shape — top three react to the
  // selected range so swapping the dropdown updates this whole grid.
  const metrics = [
    {
      label: `Revenue ${rangeLabel}`,
      getVal: (d) => d.kpis.revenue?.[revSlice] || 0,
      getLy:  (d) => d.kpis.revenue?.[`${revSlice}_py`] || 0,
      fmt: fmtFull,
    },
    {
      label: `Revenue ${stepDownLabel}`,
      getVal: (d) => d.kpis.revenue?.[stepDownKey] || 0,
      getLy:  (d) => d.kpis.revenue?.[`${stepDownKey}_py`] || 0,
      fmt: fmtFull,
    },
    {
      label: "Revenue Today",
      getVal: (d) => d.kpis.revenue?.today || 0,
      getLy:  (d) => d.kpis.revenue?.today_py || 0,
      fmt: fmtK,
    },
    {
      label: "Dogs On-Site",
      getVal: (d) => (d.kpis.occupancy?.current?.boarding || 0) + (d.kpis.occupancy?.current?.daycare || 0),
      getLy:  (d) => (d.kpis.occupancy?.prior_year?.boarding || 0) + (d.kpis.occupancy?.prior_year?.daycare || 0),
      fmt: (n) => Number(n).toLocaleString(),
    },
    {
      label: "Boarding",
      getVal: (d) => d.kpis.occupancy?.current?.boarding || 0,
      getLy:  (d) => d.kpis.occupancy?.prior_year?.boarding || 0,
      fmt: (n) => Number(n).toLocaleString(),
    },
    {
      label: "Daycare",
      getVal: (d) => d.kpis.occupancy?.current?.daycare || 0,
      getLy:  (d) => d.kpis.occupancy?.prior_year?.daycare || 0,
      fmt: (n) => Number(n).toLocaleString(),
    },
    {
      label: "Leads YTD",
      getVal: (d) => (d.leads?.total_leads) || 0,
      getLy:  () => 0, // backend doesn't yet expose prior-year leads
      fmt: (n) => Number(n).toLocaleString(),
    },
  ];

  if (valid.length < 2) {
    return (
      <div className="text-sm text-gray-500 italic py-8 text-center">
        Need at least 2 locations with data to compare. Waiting for initial sync…
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse tabular-nums">
        <thead>
          <tr className="border-b text-xs uppercase tracking-wider text-gray-500">
            <th className="text-left py-3 pr-4 font-semibold w-40">Metric</th>
            {valid.map((loc, i) => (
              <th key={loc.id} className="text-left py-3 font-semibold pl-3" colSpan={2}>
                <div className="flex items-center gap-1.5">
                  <span className={`h-2 w-2 rounded-full ${DOT_COLORS[i % DOT_COLORS.length]}`} />
                  {loc.name}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {metrics.map(m => {
            const values = valid.map(loc => m.getVal(loc.data));
            const lyValues = valid.map(loc => m.getLy(loc.data));
            const max = Math.max(...values, ...lyValues, 1);
            return (
              <tr key={m.label} className="border-b last:border-0 hover:bg-gray-50">
                <td className="py-3 pr-4 font-medium text-gray-700 align-middle">{m.label}</td>
                {valid.map((loc, i) => {
                  const val = values[i];
                  const ly = lyValues[i];
                  const widthPct = (val / max) * 100;
                  const lyTickPct = (ly / max) * 100;
                  const yoyPct = ly !== 0 ? ((val - ly) / ly * 100) : null;
                  const barColor = BAR_COLORS[i % BAR_COLORS.length];
                  return (
                    <Fragment key={loc.id}>
                      <td className="py-3 pr-2 align-middle min-w-[180px]">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-6 bg-slate-100 rounded relative overflow-hidden">
                            <div
                              className={`h-full ${barColor} rounded transition-all`}
                              style={{ width: `${widthPct}%` }}
                            />
                            {ly > 0 && (
                              <div
                                className="absolute top-0 bottom-0 w-0.5 bg-gray-700"
                                style={{ left: `${lyTickPct}%` }}
                                title={`Last year: ${m.fmt(ly)}`}
                              />
                            )}
                          </div>
                          <span className="text-xs whitespace-nowrap min-w-[80px] text-right font-bold text-gray-900">
                            {m.fmt(val)}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 pr-4 align-middle">
                        <div className="flex flex-col items-end leading-tight">
                          {yoyPct != null ? (
                            <>
                              <ChangeChip value={parseFloat(yoyPct.toFixed(1))} />
                              <span className="text-[10px] text-gray-400 mt-0.5 whitespace-nowrap">
                                vs {m.fmt(ly)}
                              </span>
                            </>
                          ) : (
                            <span className="text-[10px] text-gray-400 italic">no LY data</span>
                          )}
                        </div>
                      </td>
                    </Fragment>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="mt-3 text-xs text-gray-500 flex items-center gap-4 flex-wrap">
        {valid.map((loc, i) => (
          <span key={loc.id} className="flex items-center gap-1.5">
            <span className={`h-2 w-2 rounded-full ${DOT_COLORS[i % DOT_COLORS.length]}`} />
            {loc.name}
          </span>
        ))}
        <span className="text-gray-400">·</span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-0.5 h-3 bg-gray-700" />
          Vertical tick = last year same period
        </span>
        <span className="text-gray-400">·</span>
        <span>Δ = year-over-year change</span>
      </div>
    </div>
  );
}
